// eslint-disable-next-line @typescript-eslint/ban-ts-comment -- untyped since it was written; typing it is its own change
// @ts-nocheck

import * as React from "react";
import {
	Box,
	Button,
	Card,
	CardContent,
	FormControl,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { PlusIcon } from "@phosphor-icons/react/dist/ssr/Plus";

import { ChipsFreeSolo } from "../chips-free-solo";
import { CustomDatePicker } from "../customDatePicker/custom-date-picker";
import { FieldType } from "../dynamicField/types";
import { FilterCondition, FilterOperator } from "./types";

interface FilterBuilderProps {
	conditions: FilterCondition[];
	onChange: (conditions: FilterCondition[]) => void;
	availableFields: FieldType[];
}

const getOperatorLabel = (operator: FilterOperator): string => {
	const labels: Record<FilterOperator, string> = {
		[FilterOperator.EQUALS]: "Equals",
		[FilterOperator.NOT_EQUALS]: "Not Equals",
		[FilterOperator.GREATER_THAN]: "Greater Than",
		[FilterOperator.GREATER_THAN_OR_EQUALS]: "Greater Than or Equals",
		[FilterOperator.LESS_THAN]: "Less Than",
		[FilterOperator.LESS_THAN_OR_EQUALS]: "Less Than or Equals",
		[FilterOperator.LIKE]: "Like",
		[FilterOperator.NOT_LIKE]: "Not Like",
		[FilterOperator.IN]: "In",
		[FilterOperator.NOT_IN]: "Not In",
		[FilterOperator.AND]: "And",
		[FilterOperator.OR]: "Or",
		[FilterOperator.IS_NULL]: "Is Empty",
		[FilterOperator.IS_NOT_NULL]: "Is Not Empty",
		[FilterOperator.ANY]: "Any",
		[FilterOperator.NOT_ANY]: "Not Any",
	};
	return labels[operator] || operator;
};

const getAvailableOperators = (fieldType: string): FilterOperator[] => {
	switch (fieldType) {
		case "text": {
			return [
				FilterOperator.EQUALS,
				FilterOperator.NOT_EQUALS,
				FilterOperator.LIKE,
				FilterOperator.NOT_LIKE,
				FilterOperator.IS_NULL,
				FilterOperator.IS_NOT_NULL,
			];
		}
		case "number": {
			return [
				FilterOperator.EQUALS,
				FilterOperator.NOT_EQUALS,
				FilterOperator.GREATER_THAN,
				FilterOperator.GREATER_THAN_OR_EQUALS,
				FilterOperator.LESS_THAN,
				FilterOperator.LESS_THAN_OR_EQUALS,
				FilterOperator.IS_NULL,
				FilterOperator.IS_NOT_NULL,
			];
		}
		case "select": {
			return [
				FilterOperator.EQUALS,
				FilterOperator.NOT_EQUALS,
				FilterOperator.IN,
				FilterOperator.NOT_IN,
				FilterOperator.IS_NULL,
				FilterOperator.IS_NOT_NULL,
			];
		}
		case "date": {
			return [
				FilterOperator.EQUALS,
				FilterOperator.NOT_EQUALS,
				FilterOperator.GREATER_THAN,
				FilterOperator.GREATER_THAN_OR_EQUALS,
				FilterOperator.LESS_THAN,
				FilterOperator.LESS_THAN_OR_EQUALS,
				FilterOperator.IS_NULL,
				FilterOperator.IS_NOT_NULL,
			];
		}
		default: {
			return [FilterOperator.EQUALS, FilterOperator.NOT_EQUALS];
		}
	}
};

const requiresValue = (operator: FilterOperator): boolean => {
	return ![FilterOperator.IS_NULL, FilterOperator.IS_NOT_NULL, FilterOperator.ANY, FilterOperator.NOT_ANY].includes(
		operator
	);
};

const requiresArrayValue = (operator: FilterOperator): boolean => {
	return [FilterOperator.IN, FilterOperator.NOT_IN].includes(operator);
};

export function FilterBuilder({ conditions, onChange, availableFields }: FilterBuilderProps): React.JSX.Element {
	const addCondition = () => {
		const newCondition: FilterCondition = {
			operator: FilterOperator.EQUALS,
			field: availableFields[0]?.name || "",
			value: "",
		};
		onChange([...conditions, newCondition]);
	};

	const removeCondition = (index: number) => {
		const newConditions = conditions.filter((_, i) => i !== index);
		onChange(newConditions);
	};

	const updateCondition = (index: number, field: keyof FilterCondition, value: any) => {
		const newConditions = [...conditions];
		newConditions[index] =
			field === "value"
				? { ...newConditions[index], [field]: value }
				: field === "field"
					? { ...newConditions[index], [field]: value, value: "", operator: FilterOperator.EQUALS }
					: { ...newConditions[index], operator: value, value: "" };
		onChange(newConditions);
	};

	const getFieldType = (fieldValue: string): string => {
		const field = availableFields.find((f) => f.name === fieldValue);
		return field?.type || "text";
	};

	return (
		<Stack spacing={2}>
			{conditions.length === 0 ? (
				<Typography color="text.secondary" variant="body2" sx={{ textAlign: "center", py: 2 }}>
					No conditions added. Click &quot;Add Condition&quot; to start filtering.
				</Typography>
			) : (
				<Stack spacing={2}>
					{conditions.map((condition, index) => {
						const fieldType = getFieldType(condition.field || "");
						const availableOperators = getAvailableOperators(fieldType);
						const needsValue = requiresValue(condition.operator);
						const needsArrayValue = requiresArrayValue(condition.operator);
						const field = availableFields.find((f) => f.name === condition.field);

						return (
							<Card key={index} variant="outlined" sx={{ position: "relative" }}>
								<CardContent sx={{ pb: "16px !important" }}>
									<Grid container spacing={2} alignItems="center">
										<Grid size={{ xs: 12, md: 6 }}>
											<FormControl fullWidth>
												<InputLabel>Field</InputLabel>
												<Select
													value={condition.field || ""}
													onChange={(e) => updateCondition(index, "field", e.target.value)}
													label="Field"
												>
													{availableFields.map((field) => (
														<MenuItem key={field.name} value={field.name} sx={{ fontSize: "16px !important" }}>
															{field.label}
														</MenuItem>
													))}
												</Select>
											</FormControl>
										</Grid>

										<Grid size={{ xs: 12, md: 6 }}>
											<FormControl fullWidth>
												<InputLabel>Operator</InputLabel>
												<Select
													value={condition.operator}
													onChange={(e) => updateCondition(index, "operator", e.target.value)}
													label="Operator"
												>
													{availableOperators.map((operator) => (
														<MenuItem key={operator} value={operator} sx={{ fontSize: "16px !important" }}>
															{getOperatorLabel(operator)}
														</MenuItem>
													))}
												</Select>
											</FormControl>
										</Grid>

										<Grid size={{ xs: 12, md: 12 }} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
											{needsValue && (
												<Box sx={{ flex: 1 }}>
													{needsArrayValue ? (
														<ChipsFreeSolo
															placeholder="Type and press Enter to add values"
															value={condition.value || []}
															setValue={(value) => updateCondition(index, "value", value)}
															withOptions
															getOptionsUrl={field?.getOptionsUrl}
															fetchLabel={field?.fetchLabel}
															fetchValue={field?.fetchValue}
															isTags={field?.name === "tags"}
															defaultOptions={field?.options?.options}
															label="Value"
														/>
													) : !needsArrayValue && field.type === "select" ? (
														<ChipsFreeSolo
															placeholder="Type and press Enter to add values"
															value={condition.value || []}
															setValue={(value) => updateCondition(index, "value", value)}
															withOptions
															maxValues={1}
															getOptionsUrl={field?.getOptionsUrl}
															fetchLabel={field?.fetchLabel}
															fetchValue={field?.fetchValue}
															isTags={field?.name === "tags"}
															defaultOptions={field?.options?.options}
															label="Value"
														/>
													) : field.type === "date" ? (
														<CustomDatePicker
															label="Value"
															value={condition.value as string}
															setValue={(value) => updateCondition(index, "value", value)}
														/>
													) : field.type === "boolean" ? (
														<>
															<InputLabel>Value</InputLabel>
															<Select
																value={condition.value as string}
																fullWidth
																label="Value"
																sx={{
																	color: condition.value === "" ? "var(--mui-palette-text-secondary)" : "inherit",
																	fontSize: "16px !important",
																}}
																onChange={(e) => updateCondition(index, "value", e.target.value)}
															>
																<MenuItem value="" disabled sx={{ fontSize: "16px !important" }}>
																	Select a value
																</MenuItem>
																<MenuItem value="true" sx={{ fontSize: "16px !important" }}>
																	True
																</MenuItem>
																<MenuItem value="false" sx={{ fontSize: "16px !important" }}>
																	False
																</MenuItem>
															</Select>
														</>
													) : (
														<TextField
															fullWidth
															label="Value"
															value={condition.value as string}
															placeholder="Enter value"
															onChange={(e) => updateCondition(index, "value", e.target.value)}
															type={fieldType === "number" ? "number" : fieldType === "date" ? "date" : "text"}
														/>
													)}
												</Box>
											)}

											<Button
												onClick={() => removeCondition(index)}
												variant="outlined"
												color="error"
												sx={{ alignSelf: "flex-end" }}
												size="small"
											>
												Remove
											</Button>
										</Grid>
									</Grid>
								</CardContent>
							</Card>
						);
					})}
				</Stack>
			)}

			<Button
				startIcon={<PlusIcon />}
				onClick={addCondition}
				variant="outlined"
				size="small"
				sx={{ width: "max-content", alignSelf: "flex-end" }}
			>
				Add Condition
			</Button>

			{/* {conditions.length > 0 && (
				<Box sx={{ mt: 2 }}>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
						Generated Filter JSON:
					</Typography>
					<Box
						sx={{
							p: 2,
							bgcolor: "grey.100",
							borderRadius: 1,
							fontFamily: "monospace",
							fontSize: "0.875rem",
							overflow: "auto",
						}}
					>
						<pre>{JSON.stringify(conditions, null, 2)}</pre>
					</Box>
				</Box>
			)} */}
		</Stack>
	);
}
